import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Building2, LogOut, Mail, Phone, FileText, Target, UserCircle2, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { getMyCompanyData } from "@/lib/bolag-portal.functions";

export const Route = createFileRoute("/bolag-portal")({
  head: () => ({
    meta: [
      { title: "Bolagsportal — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BolagPortalPage,
});

const MONTH_NAMES = [
  "Januari", "Februari", "Mars", "April", "Maj", "Juni",
  "Juli", "Augusti", "September", "Oktober", "November", "December",
];

const STATUS_LABEL: Record<string, string> = {
  planerad: "Planerad",
  pågående: "Pågående",
  klar: "Klar",
  pausad: "Pausad",
};

function BolagPortalPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/bolag-login" });
  }, [loading, user, navigate]);

  const fetchData = useServerFn(getMyCompanyData);
  const { data, isLoading, error } = useQuery({
    queryKey: ["bolag-portal", user?.id],
    queryFn: () => fetchData(),
    enabled: !!user,
  });

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Laddar…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-16 border-b border-border flex items-center px-6 md:px-10 gap-4 sticky top-0 bg-background/80 backdrop-blur z-10">
        <div className="flex items-center gap-2.5">
          <div className="size-8 rounded-lg bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-3.5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-base leading-none">Inkubera</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Bolagsportal
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground hidden md:inline">{user.email}</span>
          <button
            onClick={() => supabase.auth.signOut().then(() => navigate({ to: "/" }))}
            className="h-9 px-3 rounded-lg bg-secondary text-sm font-medium hover:bg-secondary/70 inline-flex items-center gap-1.5"
          >
            <LogOut className="size-4" /> Logga ut
          </button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-5xl mx-auto">
        {isLoading && <div className="text-sm text-muted-foreground">Laddar bolagsdata…</div>}
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            {(error as Error).message}
          </div>
        )}

        {!isLoading && !error && (data?.companies?.length ?? 0) === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center">
            <Building2 className="size-8 mx-auto mb-3 opacity-50" />
            <div className="font-medium">Inget bolag kopplat till ditt konto</div>
            <p className="text-sm text-muted-foreground mt-2">
              Be Inkubera-administratören att koppla din e-post till ert bolag.
            </p>
          </div>
        )}

        {(data?.companies ?? []).map(({ company, reports, milestones, coaches }) => (
          <section key={company.id} className="space-y-8">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
                Bolagsprofil
              </div>
              <h1 className="font-display text-3xl">{company.name}</h1>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-secondary">{company.fas}</span>
                <span className="px-2.5 py-1 rounded-full bg-secondary">{company.status}</span>
                {company.municipality && (
                  <span className="px-2.5 py-1 rounded-full bg-secondary">{company.municipality}</span>
                )}
              </div>
              {(company.contact_name || company.contact_email) && (
                <div className="mt-4 text-sm text-muted-foreground">
                  Kontakt: {company.contact_name}
                  {company.contact_email && ` · ${company.contact_email}`}
                </div>
              )}
            </div>

            {(coaches ?? []).length > 0 && (
              <div>
                <h2 className="font-display text-lg flex items-center gap-2 mb-3">
                  <UserCircle2 className="size-4" /> Tilldelad coach
                </h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {(coaches ?? []).map((c, i) => (
                    <div key={i} className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                      {c.photo_url ? (
                        <img src={c.photo_url} alt="" className="size-12 rounded-full object-cover" />
                      ) : (
                        <div className="size-12 rounded-full bg-secondary flex items-center justify-center text-sm font-medium">
                          {(c.full_name ?? "?").split(" ").map((n: string) => n[0]).slice(0, 2).join("")}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.full_name}</div>
                        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-foreground">
                            <Mail className="size-3" /> {c.email}
                          </a>
                          {c.phone && (
                            <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-foreground">
                              <Phone className="size-3" /> {c.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(milestones ?? []).length > 0 && (
              <div>
                <h2 className="font-display text-lg flex items-center gap-2 mb-3">
                  <Target className="size-4" /> Milstolpar
                </h2>
                <div className="space-y-2">
                  {(milestones ?? []).map((m) => (
                    <div key={m.id} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium">{m.title}</div>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary">
                          {STATUS_LABEL[m.status] ?? m.status}
                        </span>
                      </div>
                      {m.description && (
                        <p className="text-sm text-muted-foreground mt-1">{m.description}</p>
                      )}
                      {m.target_date && (
                        <div className="text-xs text-muted-foreground mt-2">
                          Måldatum: {m.target_date}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-display text-lg flex items-center gap-2 mb-3">
                <FileText className="size-4" /> Månadsrapporter från coach
              </h2>
              {(reports ?? []).length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-sm text-muted-foreground text-center">
                  Inga månadsrapporter inlämnade ännu.
                </div>
              ) : (
                <div className="space-y-3">
                  {(reports ?? []).map((r) => (
                    <div key={r.id} className="rounded-xl border border-border bg-card p-5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-medium">
                            {MONTH_NAMES[r.month - 1]} {r.year}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Inlämnad av {r.coach_name ?? "coach"} · {new Date(r.submitted_at).toLocaleDateString("sv-SE")}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 text-[10px]">
                          {r.brl != null && <Badge label="BRL" value={r.brl} />}
                          {r.tmrl != null && <Badge label="TMRL" value={r.tmrl} />}
                          {r.srl != null && <Badge label="SRL" value={r.srl} />}
                          {r.crl != null && <Badge label="CRL" value={r.crl} />}
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 text-sm">
                        {r.summary && <Field label="Sammanfattning" value={r.summary} />}
                        {r.progress && <Field label="Framsteg" value={r.progress} />}
                        {r.obstacles && <Field label="Hinder" value={r.obstacles} />}
                        {r.next_milestones && <Field label="Nästa milstolpar" value={r.next_milestones} />}
                        {r.support_needed && <Field label="Stöd som behövs" value={r.support_needed} />}
                        {r.trl_comment && <Field label="TRL-kommentar" value={r.trl_comment} />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}

function Badge({ label, value }: { label: string; value: number }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary uppercase tracking-wider">
      {label} <span className="font-medium text-foreground">{value}</span>
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
      <div className="whitespace-pre-wrap">{value}</div>
    </div>
  );
}
